import React, { useEffect, useState } from "react";
import Common from "us.common";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  NonEmailDays,
  NonSMSDays,
  NonPrintingDays,
  NonEHFDays,
} from "./Components";
import { CalendarOutlined, LeftOutlined, RightOutlined } from "us.icons";
import {
  NonProductionDayType,
  YearNavigation,
} from "us.collection.admin/constants";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { nonProductionDays_New } from "us.collection.admin/actions";
import { NonProductionDayTabs } from "./Constants"; 
import _ from "lodash";
import "./NonProductionDays.scss";
import dayjs from "dayjs";

const { calendar, redDays } = nonProductionDays_New;
const {
  $Tabs,
  $Button,
  $Popconfirm,
  $PageHeader,
  $Divider,
  $Affix,
  $DatePicker,
  $Tooltip,
  $Col,
  $Row,
} = Common.Components;

/**
 * @description Non Productions Days Home Component
 * @link Design document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3049914453/Non+Email+Days+UI+Design
 * @author Rajitha Sanjayamal <rajithasa@unicorn-solutions.com>
 * @since 21/07/2022
 */
const NonProductionsDays: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const goBack = navigate(-1);
  const { manageTabs, selectedCriteria, getNonProductionDays } = props;
  const [isOpenYearPicker, setIsOpenYearPicker] = useState<boolean>(false);

  useEffect(() => {
    getNonProductionDays &&
      getNonProductionDays({ year: dayjs().format("YYYY") });
  }, []);

  /**
   * @function
   * @description handle Tab Click
   * @param {string} activeKey current tab
   */
  const handleTab = (activeKey: string) => {
    manageTabs && manageTabs({ selectedCriteria: activeKey });
  };

  const getTabContent = (type: NonProductionDayType) => {
    switch (type) {
      case NonProductionDayType.Non_EHF:
        return <NonEHFDays />;
      case NonProductionDayType.Non_SMS:
        return <NonSMSDays />;
      case NonProductionDayType.Non_Printing:
        return <NonPrintingDays />;
      case NonProductionDayType.Non_Email:
        return <NonEmailDays />;
      default:
        return <NonPrintingDays />;
    }
  };

  /**
   * @function
   * @description handle year navigation
   * @param {string} step step type (next, previous)
   */
  const handleYearNavigation = (
    step: string,
    setFieldValue: any,
    currentYear: any
  ) => {
    let selectedYear = currentYear;
    if (step === YearNavigation.NEXT) {
      selectedYear = (parseInt(selectedYear) + 1).toString();
    } else {
      selectedYear = (parseInt(selectedYear) - 1).toString();
    }
    setFieldValue("selectedYear", selectedYear);
    getNonProductionDays && getNonProductionDays({ year: selectedYear });
  };

  /**
   * @function
   * @description handle year selection
   * @param {string} year selected year
   */
  const handleYearSelection = (year: string, setFieldValue: any) => {
    setFieldValue("selectedYear", year);
    getNonProductionDays && getNonProductionDays({ year: year });
  };

  return (
    <Formik
      initialValues={{ selectedYear: dayjs().year() }}
      onSubmit={() => { }}
      enableReinitialize
    >
      {({
        values,
        handleChange,
        handleBlur,
        isValidating,
        resetForm,
        setFieldValue,
        ...rest
      }: any) => (
        <div className="non-production-days space-content">
          <$Affix offsetTop={48}>
            <div className="page-header header-border">
              <div className="d-flex flex-row align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <$PageHeader
                    className="px-0"
                    onBack={() => navigate(-1)
                      //  goBack
                    }
                    title={t(
                      "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.NON_PRODUCTION_DAYS"
                    )}
                  />
                  <$Divider className="bui-devider" type="vertical" />

                  <div className="ml-3">
                    <div className="year-picker-custom d-flex align-items-center">
                      <$DatePicker
                        className="year-pick"
                        name="selectedYear"
                        picker="year"
                        open={isOpenYearPicker}
                        onChange={(date: any) => {
                          handleYearSelection(
                            date.year().toString(),
                            setFieldValue
                          );
                        }}
                        popupClassName={"year-picker-custom"}
                        onOpenChange={(open: boolean) =>
                          setIsOpenYearPicker(open)
                        }
                      />
                      <$Button
                        size="small"
                        onClick={() =>
                          handleYearNavigation(
                            YearNavigation.PREVIOUS,
                            setFieldValue,
                            values?.selectedYear
                          )
                        }
                      >
                        <LeftOutlined />
                      </$Button>

                      <div className="px-4 current-year">
                        <strong>{values?.selectedYear}</strong>
                      </div>
                      <$Button
                        size="small"
                        onClick={() =>
                          handleYearNavigation(
                            YearNavigation.NEXT,
                            setFieldValue,
                            values?.selectedYear
                          )
                        }
                      >
                        <RightOutlined />
                      </$Button>
                      <$Button
                        size="small"
                        className="ml-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isOpenYearPicker) {

                            setIsOpenYearPicker(true);
                          }
                        }}
                      >
                        <CalendarOutlined />
                      </$Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </$Affix>
          <div className="mb-5">
            <$Row gutter={16}>
              <$Col xl={{ span: 24 }} xxl={{ span: 24 }}>
                <$Tabs
                  className="bui-tabs"
                  defaultActiveKey={NonProductionDayType.Non_Printing}
                  activeKey={selectedCriteria}
                  onChange={(activeKey: string) => handleTab(activeKey)}
                  items={NonProductionDayTabs.map(
                    ({ name, description, type }: typeof NonProductionDayTabs[0]) => ({
                      key: type,
                      label: (
                        <$Tooltip placement="top" title={t(description)}>
                          {t(name)}
                        </$Tooltip>
                      ),
                      children: getTabContent(type),
                    })
                  )}
                />
              </$Col>
            </$Row>
          </div>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { nonProductionDays } = state;
  const { redDays, selectedCriteria, scheduleTime, isSubmitting } =
    nonProductionDays;
  return { redDays, selectedCriteria, scheduleTime, isSubmitting };
};

const mapDispatchToProps = {
  manageTabs: calendar.manageTabs,
  updateNonProduction: calendar.update,
  getNonProductionDays: redDays.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(NonProductionsDays);
